// eslint-disable-next-line import/no-cycle
import { UnknownError } from '@cian/peperrors';

import { CHATBOT_ID } from '../../constants/chatbotId';
import { GuardError } from '../../errors/GuardError';
import { currentOpenChatIDSelector } from '../../selectors';
import { checkUsersSession } from '../../services/checkUsersSession';
import { selectSession } from '../../slices/sessionSlice';
import { setSocketStatusFailed, setSocketStatusPending } from '../../slices/socketStatusSlice';
import { ERequestApiStatus } from '../../types/appState';
import { EExceptionDomain } from '../../types/exceptionDomains';
import { TThunkAction } from '../../types/redux/thunk';
import { ETelemetryEvents } from '../../types/telemetry';
import { getGenericLikeError } from '../../utils/logging/getGenericLikeError';
import { retryWithDelay } from '../../utils/retryWithDelay';
import { synchronizeChatsV2 } from '../chatList/synchronizeChatsV2';
import { updateMessages } from '../messages/updateMessages';
import { registerWsSession, registerWsSessionFailed } from '../wsSession';

const RETRIES_COUNT = 4;

/**
 * Открытие канала WS
 */
export const openEventChannel =
  (reconnect: boolean = false): TThunkAction<Promise<void>> =>
  async (dispatch, getState, { logger, telemetry, custom: { socket } }) => {
    if (!socket) {
      return;
    }

    const { eventChannel } = socket;

    const state = getState();
    const session = selectSession(state);

    dispatch(setSocketStatusPending());

    // Если соединение не закрыто
    // TODO: избавиться от блока
    if (eventChannel.hasInstance() && !eventChannel.isChannelClosed()) {
      await dispatch(registerWsSession());

      // Если истекла сессия (стоит пересмотреть логику)
      if (session.status !== ERequestApiStatus.Succeed && session.status !== ERequestApiStatus.Pending) {
        telemetry.increment(ETelemetryEvents.ChatWsSessionExpire);

        const exception = new GuardError({
          domain: EExceptionDomain.Ws,
          message: 'При попытке открытия соединения по вебсокету сессия истекла, соединение уже открыто',
        });

        logger.warning(exception);

        await dispatch(checkUsersSession());
      }

      return;
    }

    // Если истекла сессия (стоит пересмотреть логику)
    if (session.status !== ERequestApiStatus.Succeed && session.status !== ERequestApiStatus.Pending) {
      telemetry.increment(ETelemetryEvents.ChatWsSessionExpire);

      const exception = new GuardError({
        domain: EExceptionDomain.Ws,
        message: 'При попытке открытия соединения по вебсокету сессия истекла, соединение закрыто',
      });

      logger.warning(exception);

      await dispatch(checkUsersSession());
    }

    const startTime = new Date().getTime();

    try {
      // Делаем 4 попытки открыть соединение с задержкой в 1, 2, 3, 4 секунды
      await retryWithDelay({
        asyncFn: () => {
          if (eventChannel.isChannelClosed()) {
            return eventChannel.connectToEventChannel() as Promise<void>;
          }

          return Promise.resolve();
        },
        onError: error => {
          telemetry.increment(ETelemetryEvents.ChatWsSocketError);

          const connectError = new UnknownError({
            domain: EExceptionDomain.Ws,
            message: 'Ошибка при попытке подключения к вебсокету',
            details: error,
          });

          logger.error(connectError);
        },
        retryCount: RETRIES_COUNT,
      });
    } catch (e) {
      dispatch(setSocketStatusFailed());
      dispatch(registerWsSessionFailed());

      const errorToLog = getGenericLikeError({
        error: e,
        domain: EExceptionDomain.Ws,
        message: 'Ошибка при открытии соединения по вебсокету',
      });

      logger.error(errorToLog);

      telemetry.increment(ETelemetryEvents.ChatWsSessionFail);

      return;
    }

    await dispatch(registerWsSession(startTime));

    if (reconnect) {
      const currentOpenChatID = currentOpenChatIDSelector(state);

      if (currentOpenChatID !== CHATBOT_ID && currentOpenChatID) {
        dispatch(updateMessages(currentOpenChatID, true));
      }

      dispatch(synchronizeChatsV2());
    }
  };
